import { cn } from '@atlas/ui-primitives'
import { ImageIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { UploadDropzone } from './upload-dropzone'

export interface AssetSlotProps {
	/** What belongs here — "Phone story mockup". Also the file input's accessible name. */
	label: string
	/** Secondary line under the slot, e.g. "Story · 9:16". */
	caption?: ReactNode
	/**
	 * Accept an uploaded file. Omit to make the slot fill-by-browse only — a
	 * surface where assets are chosen from the library, never added from disk.
	 */
	onFiles?: (files: File[]) => void
	/**
	 * Pick an existing asset instead of uploading one. Omit to make the slot
	 * upload-only.
	 *
	 * Offering both is the common case: an asset that already exists in the
	 * library should not have to be re-uploaded to fill a slot.
	 */
	onBrowse?: () => void
	/** Label for the browse action, when the default reads wrong for the surface. */
	browseLabel?: string
	/** The asset occupying this slot. Rendered instead of the empty state. */
	children?: ReactNode
	/** Square by default, matching a mockup slot; `auto` lets the content set the height. */
	aspect?: 'square' | 'auto'
	accept?: string
	isDisabled?: boolean
}

/** The empty state when the slot cannot be filled from disk — browse, or nothing at all. */
function StaticSlot({ label, isDisabled, onBrowse, browseLabel, aspect }: AssetSlotProps) {
	return (
		<div
			className={cn(
				'flex flex-col items-center justify-center gap-ds-2 rounded-ds-control border border-dashed border-ds-border bg-ds-surface p-ds-6 text-center',
				aspect === 'square' && 'aspect-square',
				isDisabled && 'opacity-60',
			)}
		>
			<ImageIcon className="h-8 w-8 text-ds-text-subtle" aria-hidden="true" />
			<span className="text-ds-sm text-ds-text">{label}</span>
			{onBrowse && (
				<button
					type="button"
					disabled={isDisabled}
					onClick={onBrowse}
					className="text-ds-xs text-ds-primary underline underline-offset-2 hover:text-ds-text disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring"
				>
					{browseLabel ?? 'Browse existing'}
				</button>
			)}
		</div>
	)
}

/**
 * One slot in a layout, holding one asset.
 *
 * A slot is not a placeholder: it names the asset it expects and can be FILLED,
 * by upload or by picking something already in the library. Which paths are
 * offered is configuration — pass `onFiles`, `onBrowse`, or both — because the
 * same slot appears on surfaces where adding from disk is allowed and on
 * surfaces where assets may only be chosen from what exists.
 *
 * The square footprint is deliberate: it previews the shape of what will fill
 * it, so a grid of empty slots already reads as the composition it will become.
 *
 * Single-file by design. A slot holds one asset; dropping several would leave
 * the caller to decide which one wins.
 */
export function AssetSlot(props: AssetSlotProps) {
	const { label, caption, onFiles, onBrowse, browseLabel, children, aspect = 'square', accept, isDisabled } = props

	const body = children ? (
		<div
			className={cn(
				'overflow-hidden rounded-ds-control border border-ds-border bg-ds-surface',
				aspect === 'square' && 'aspect-square',
			)}
		>
			{children}
		</div>
	) : onFiles ? (
		<div className={cn(aspect === 'square' && 'aspect-square', '[&>label]:h-full')}>
			<UploadDropzone
				label={label}
				onFiles={onFiles}
				accept={accept}
				isDisabled={isDisabled}
				multiple={false}
				variant="panel"
			>
				<ImageIcon className="h-8 w-8 text-ds-text-subtle" aria-hidden="true" />
				<span className="text-ds-sm text-ds-text">{label}</span>
				{onBrowse && (
					// Inside the label, so it must not toggle the file input: a click here
					// means "choose an existing asset", not "open the file picker".
					<button
						type="button"
						disabled={isDisabled}
						onClick={(event) => {
							event.preventDefault()
							onBrowse()
						}}
						className="text-ds-xs text-ds-primary underline underline-offset-2 hover:text-ds-text disabled:opacity-60"
					>
						{browseLabel ?? 'Browse existing'}
					</button>
				)}
			</UploadDropzone>
		</div>
	) : (
		<StaticSlot {...props} aspect={aspect} />
	)

	return (
		<div className="space-y-1 text-center">
			{body}
			{caption && <p className="text-ds-xs text-ds-text-subtle">{caption}</p>}
		</div>
	)
}
